// Reading the store without taking the whole board with you.
//
// `useGameSnapshot` is the one bridge and stays so; this is the narrower door
// beside it. A component that only wants the log re-renders on every command
// either way — the snapshot is replaced wholesale — but a component that wants
// something *derived* from the board should not re-render when the derivation
// has not moved, and there was no way to say that.
//
// **The cache is the whole mechanism.** `useSyncExternalStore` re-renders
// whenever the value its `getSnapshot` returns stops being reference-equal to
// the last one, so a reader that derives per call is an infinite loop rather
// than a slow path — the same rule `InsetStore.getSnapshot` is written to
// (`kit/InsetRegistry.tsx`). So the reader is built once per store, remembers
// the snapshot it last answered about, and hands back the *previous* value
// whenever the caller's own `isEqual` says nothing changed.
//
// Derivation does not live here. `hotbarModel`, `selectionModel`, `handModel`
// and `useEconomy` are memoised models over a whole board and stay where they
// are; a selector is for picking a field out, not for computing one.

import { useMemo, useRef, useSyncExternalStore } from "react";

import type { Command, GameState } from "src/engine/rules/commands";
import type { GameSnapshot, HistoryView } from "src/engine/game/GameStore";
import { useStore } from "./useGameStore";

/**
 * What to pull out of a snapshot, and when two answers count as the same.
 *
 * An object rather than two arguments because `cachedSelect` is built **once**
 * per store and the caller's closures are rebuilt every render — so the reader
 * holds this object and the hook writes the fields, rather than the reader
 * closing over a function that is stale by its second call.
 */
export interface Selection<T> {
  select: (snapshot: GameSnapshot) => T;
  /**
   * Absent means identity, which is the right default: every field on a
   * snapshot is replaced wholesale by the store, so `===` already answers
   * "has this moved" for anything read straight off one.
   */
  isEqual?: (a: T, b: T) => boolean;
}

/**
 * A reader that answers about the current snapshot and remembers the last one.
 *
 * Two things are cached and they are different claims. **The snapshot** — the
 * same snapshot asked twice is answered without running `select` again, which
 * is what makes `getSnapshot` cheap enough to be called on every render and
 * every store notification. **The value** — a new snapshot whose selection is
 * equal under `isEqual` hands back the *previous reference*, which is what
 * stops `useSyncExternalStore` re-rendering for a derivation that did not move.
 *
 * Pure, and takes its inputs as arguments, so the caching is testable without
 * a renderer.
 */
export function cachedSelect<T>(
  read: () => GameSnapshot,
  lens: Selection<T>
): () => T {
  let held: { snapshot: GameSnapshot; value: T } | null = null;
  return () => {
    const snapshot = read();
    if (held !== null && held.snapshot === snapshot) return held.value;
    const next = lens.select(snapshot);
    if (held !== null && lens.isEqual?.(held.value, next)) {
      // The snapshot is remembered even though the value is not replaced, or
      // the comparison would be redone on every read of an unchanged board.
      held = { snapshot, value: held.value };
      return held.value;
    }
    held = { snapshot, value: next };
    return next;
  };
}

/**
 * **The board on screen, which is not always the live one** (§29 U4).
 *
 * A history preview draws an earlier position, and everything that *draws* —
 * the renderer, the readouts, the inspector, the hand — has to agree about
 * which board that is or the chrome describes a position nobody can see. So
 * the narrow door answers `viewed ?? state` and the wide one (`useGameSnapshot`)
 * keeps answering the live board, which is the split: **anything that plays
 * the game reads the snapshot** — the opponent driver, the run director, the
 * marches, autosave and the sounds — and anything that shows it reads this.
 */
const STATE_LENS: Selection<GameState> = {
  select: (snapshot) => snapshot.history.viewed ?? snapshot.state
};
const LOG_LENS: Selection<readonly Command[]> = { select: (snapshot) => snapshot.log };
// The store keeps this object's identity while nothing in it moves, so
// identity is the change test here too.
const HISTORY_LENS: Selection<HistoryView> = { select: (snapshot) => snapshot.history };

/**
 * The board on screen — `viewed ?? state` (§29 U4). Replaced per command, so
 * identity is a correct change test.
 */
export function useGameState(): GameState {
  const store = useStore();
  // Built once per store, never per render: `useSyncExternalStore` resubscribes
  // and re-reads whenever these move, so a fresh closure per render is a fresh
  // cache per render — which is no cache at all.
  const read = useMemo(() => cachedSelect(store.getSnapshot, STATE_LENS), [store]);
  return useSyncExternalStore(store.subscribe, read, read);
}

/**
 * What the player is looking at, when it is not the live board (§29 U4).
 *
 * Its own reader because the transport needs the *fact* of the view — how many
 * steps there are and how far behind Latest you have fallen — where every
 * other zone only needs the board it produced.
 */
export function useHistoryView(): HistoryView {
  const store = useStore();
  const read = useMemo(() => cachedSelect(store.getSnapshot, HISTORY_LENS), [store]);
  return useSyncExternalStore(store.subscribe, read, read);
}

/** The command log — the save format, the replay and the network protocol. */
export function useGameLog(): readonly Command[] {
  const store = useStore();
  const read = useMemo(() => cachedSelect(store.getSnapshot, LOG_LENS), [store]);
  return useSyncExternalStore(store.subscribe, read, read);
}

/**
 * One thing off the board, re-rendering only when that thing moves.
 *
 * `isEqual` is what makes it worth having: `useGameSelect((s) => s.state.run?.side)`
 * is stable by identity anyway, while anything that builds a small object or a
 * list needs to say when two of them are the same.
 *
 * **The callbacks are read through a ref rather than depended on**, so the
 * ordinary way to call this — an arrow written inline — does not throw the
 * cache away on every render. That is the same discipline `useSoloOpponent`
 * keeps its plan listener with, and for the same reason: a dependency nobody
 * can keep stable restarts the machinery it is named in.
 */
export function useGameSelect<T>(
  select: (snapshot: GameSnapshot) => T,
  isEqual?: (a: T, b: T) => boolean
): T {
  const store = useStore();
  const lens = useRef<Selection<T>>({ select, isEqual });
  lens.current.select = select;
  lens.current.isEqual = isEqual;
  const held = lens.current;
  const read = useMemo(() => cachedSelect(store.getSnapshot, held), [store, held]);
  return useSyncExternalStore(store.subscribe, read, read);
}
